/*
 * Guardar en Drive desde el portal y decir cuándo llegó (O7/O8/O9, auditoría 2026-10-09).
 *
 * Ctrl+S con el foco FUERA del documento (cabecera, Aurora, compartir…) lo recibía el portal y no hacía
 * nada, y la cabecera nunca decía «Guardado». Ahora:
 *   · Ctrl+S fuera del documento pulsa el MISMO botón Guardar del motor: es exactamente un Ctrl+S dentro
 *     (el motor junta lo pendiente y lo manda a Drive). Comprobado contra el motor 9.4.
 *   · Tras cualquier petición de guardado (Ctrl+S dentro o fuera, botón Guardar) se pregunta a Drive
 *     (`GET /office/guardado/{id}`) y solo se dice «Guardado en Drive · hora» cuando Drive cambió de verdad.
 *     Los avisos de inicio y fin de guardado del motor NO sirven para eso: salen también en cada
 *     autoguardado de la coedición, mientras se escribe. Solo se usan para saber si queda algo en curso.
 *   · Si el motor avisa de que Drive no recibió el guardado (−331) o no contestó (−101), se dice y se
 *     puede reintentar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { clickInEditor } from './skin';
import { motorDe } from './editorComandos';

export const BOTON_GUARDAR = ['#slot-btn-save button', '#id-toolbar-btn-save'];
const SONDEO_MS = 1000;
const SIN_ACTIVIDAD_MS = 2000; // sin guardado en curso ni empezado en este rato: no había nada que guardar
const MAXIMO_MS = 90000;

const esCtrlS = (e) => (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key || '').toLowerCase() === 's';

/** Pide al motor que guarde. Devuelve false si el editor todavía no tiene con qué. */
export function pulsarGuardar(iframe) {
  if (clickInEditor(iframe, BOTON_GUARDAR, { hidden: true })) return true;
  const motor = motorDe(iframe);
  if (typeof motor?.asc_Save === 'function') {
    motor.asc_Save(false);
    return true;
  }
  return false;
}

function idsDeGuardado(iframe) {
  try {
    const A = iframe?.contentWindow?.Asc?.c_oAscAsyncAction || {};
    return [A.Save, A.ForceSaveButton, A.ForceSaveTimeout].filter((id) => typeof id === 'number');
  } catch {
    return [];
  }
}

/** Avisa cuando el motor empieza y termina un guardado (de Drive o de la coedición). Devuelve con qué dejar de escuchar. */
export function escucharAccionesDeGuardado(iframe, { alEmpezar, alTerminar }) {
  let parado = false;
  let reloj = null;
  let intentos = 0;
  let motor = null;
  const esGuardado = (id) => idsDeGuardado(iframe).includes(id);
  const inicio = (_tipo, id) => { if (!parado && esGuardado(id)) alEmpezar(); };
  const fin = (_tipo, id) => { if (!parado && esGuardado(id)) alTerminar(); };
  const enganchar = () => {
    if (parado) return;
    motor = motorDe(iframe);
    if (typeof motor?.asc_registerCallback !== 'function') {
      intentos += 1;
      if (intentos < 40) reloj = setTimeout(enganchar, 250);
      return;
    }
    motor.asc_registerCallback('asc_onStartAction', inicio);
    motor.asc_registerCallback('asc_onEndAction', fin);
  };
  enganchar();
  return () => {
    parado = true;
    clearTimeout(reloj);
    try {
      motor?.asc_unregisterCallback?.('asc_onStartAction', inicio);
      motor?.asc_unregisterCallback?.('asc_onEndAction', fin);
    } catch {
      /* el iframe ya no existe */
    }
  };
}

/** Ctrl+S y el botón Guardar DENTRO del motor (el motor guarda solo; aquí solo se entera el portal). */
export function escucharPedidosDeGuardado(iframe, alPedir) {
  let win = null;
  let doc = null;
  const tecla = (e) => { if (esCtrlS(e)) alPedir(); };
  const clic = (e) => { if (e.target?.closest?.(BOTON_GUARDAR.join(','))) alPedir(); };
  try {
    win = iframe.contentWindow;
    doc = iframe.contentDocument;
    win.addEventListener('keydown', tecla, true);
    doc.addEventListener('click', clic, true);
  } catch {
    return () => {};
  }
  return () => {
    try {
      win.removeEventListener('keydown', tecla, true);
      doc.removeEventListener('click', clic, true);
    } catch {
      /* el iframe ya no existe */
    }
  };
}

/** Texto de un aviso del motor, sin el HTML que trae. */
export function textoDeAviso(aviso) {
  if (aviso?.warningCode === -331) return 'Drive no recibió el último guardado. Tus cambios siguen en el editor.';
  if (aviso?.warningCode === -101) return 'Drive no respondió al editor. Tus cambios siguen en el editor.';
  return String(aviso?.warningDescription || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    || 'El editor avisó de un problema al guardar.';
}

/**
 * Estado del guardado en Drive para la cabecera.
 * `estado`: null (nada pedido) · 'guardando' · 'guardado' (`hora` = ISO de Drive) · 'al_dia' (no había
 * nada pendiente) · 'lento' · 'error'.
 */
export function useGuardadoEnDrive({ fileId, linkToken, iframeRef, ready, canEdit }) {
  const [estado, setEstado] = useState({ estado: null, hora: null });
  const antes = useRef(null); // updated_at de Drive conocido
  const sondeo = useRef(null);
  const enCurso = useRef(0);
  const ultimaActividad = useRef(0);
  const vivo = useRef(true);
  const permiso = useRef({ ready, canEdit });
  permiso.current = { ready, canEdit };

  useEffect(() => () => {
    vivo.current = false;
    clearTimeout(sondeo.current);
  }, []);

  const leer = useCallback(async () => {
    const params = linkToken ? { link: linkToken } : undefined;
    const { data } = await api.get(`/office/guardado/${fileId}`, { params, timeout: 15000 });
    return data?.updated_at || null;
  }, [fileId, linkToken]);

  const vigilar = useCallback(() => {
    clearTimeout(sondeo.current);
    const t0 = Date.now();
    setEstado({ estado: 'guardando', hora: null });
    const paso = async () => {
      if (!vivo.current) return;
      let actual = null;
      try {
        actual = await leer();
      } catch {
        /* sin red un momento: se vuelve a preguntar */
      }
      if (!vivo.current) return;
      if (actual && antes.current && actual !== antes.current) {
        antes.current = actual;
        setEstado({ estado: 'guardado', hora: actual });
        return;
      }
      if (actual && !antes.current) antes.current = actual;
      const ahora = Date.now();
      if (ahora - t0 > SIN_ACTIVIDAD_MS && enCurso.current === 0 && ahora - ultimaActividad.current > SIN_ACTIVIDAD_MS) {
        setEstado({ estado: 'al_dia', hora: antes.current });
        return;
      }
      if (ahora - t0 > MAXIMO_MS) {
        setEstado({ estado: 'lento', hora: null });
        return;
      }
      sondeo.current = setTimeout(paso, SONDEO_MS);
    };
    sondeo.current = setTimeout(paso, 800);
  }, [leer]);

  const guardarEnDrive = useCallback(() => {
    const { ready: listo, canEdit: puede } = permiso.current;
    if (!listo) {
      toast.message('Espera a que termine de abrir el documento');
      return false;
    }
    if (!puede) {
      toast.message('Este archivo es de solo lectura: no hay cambios que guardar');
      return false;
    }
    if (!pulsarGuardar(iframeRef.current)) {
      toast.error('El editor no respondió. Intenta guardar de nuevo en un momento.');
      return false;
    }
    vigilar();
    return true;
  }, [iframeRef, vigilar]);

  // Ctrl+S con el foco en el portal (fuera del iframe): antes se lo tragaba DriveEditor sin hacer nada
  const guardarRef = useRef(guardarEnDrive);
  guardarRef.current = guardarEnDrive;
  useEffect(() => {
    const onKey = (e) => {
      if (!esCtrlS(e)) return;
      e.preventDefault();
      guardarRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Con el editor listo: lo que hace el propio motor (Ctrl+S dentro, botón Guardar, autoguardados)
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!ready || !iframe) return undefined;
    leer().then((v) => { if (vivo.current && v && !antes.current) antes.current = v; }).catch(() => {});
    const quitarAcciones = escucharAccionesDeGuardado(iframe, {
      alEmpezar: () => { enCurso.current += 1; ultimaActividad.current = Date.now(); },
      alTerminar: () => { enCurso.current = Math.max(0, enCurso.current - 1); ultimaActividad.current = Date.now(); },
    });
    const quitarPedidos = escucharPedidosDeGuardado(iframe, () => { if (permiso.current.canEdit) vigilar(); });
    return () => {
      quitarAcciones();
      quitarPedidos();
      enCurso.current = 0;
    };
  }, [ready, iframeRef, leer, vigilar]);

  const alAviso = useCallback((aviso) => {
    clearTimeout(sondeo.current);
    setEstado({ estado: 'error', hora: null });
    toast.error('No se pudo guardar en Drive', {
      description: textoDeAviso(aviso),
      duration: 15000,
      action: { label: 'Reintentar', onClick: () => guardarRef.current() },
    });
  }, []);

  return { ...estado, guardarEnDrive, alAviso };
}
